import type { Role } from './roles';

export const APP_MODULES = [
  'Dashboard',
  'DossierRequests',
  'MyTasks',
  'DataBank',
  'TechnicalDocuments',
  'Pharmacopoeia',
  'Notifications',
  'Exporters',
  'Users',
  'AccessControl',
  'ActivityLog',
  'AdminDashboard',
  'Inquiries',
  'Registrations',
  'Dossiers',
  'Artwork',
  'Samples',
] as const;

export type AppModule = (typeof APP_MODULES)[number];

export const PERMISSION_ACTIONS = ['view', 'edit', 'delete', 'export'] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

export type ModulePermission = Record<PermissionAction, boolean>;
export type PermissionMap = Record<AppModule, ModulePermission>;

export const MODULE_LABELS: Record<AppModule, string> = {
  Dashboard: 'Dashboard',
  DossierRequests: 'Document Requests',
  MyTasks: 'My Tasks',
  DataBank: 'Data Bank',
  TechnicalDocuments: 'Technical Documents',
  Pharmacopoeia: 'Pharmacopoeia & Guidelines',
  Notifications: 'Notifications',
  Exporters: 'Exporters',
  Users: 'Users',
  AccessControl: 'Access Control',
  ActivityLog: 'Activity Log',
  AdminDashboard: 'Admin Dashboard',
  Inquiries: 'Inquiries',
  Registrations: 'Registrations',
  Dossiers: 'Dossiers',
  Artwork: 'Artwork',
  Samples: 'Samples',
};

/** Modules whose data is imported now but whose screens come in phase 2. */
export const PHASE_TWO_MODULES: readonly AppModule[] = ['Inquiries', 'Registrations', 'Dossiers', 'Artwork', 'Samples'];

/** Legacy `Access_Control.module` values → new module keys. */
export const LEGACY_MODULE_MAP: Record<string, AppModule> = {
  Dashboard: 'Dashboard',
  MyTask: 'MyTasks',
  Inquiry: 'Inquiries',
  Registration: 'Registrations',
  Dossier: 'Dossiers',
  Artwork: 'Artwork',
  Samples: 'Samples',
  Documents: 'DataBank',
  DossierRequisition: 'DossierRequests',
  Notifications: 'Notifications',
  Exporters: 'Exporters',
  ActivityLog: 'ActivityLog',
};

type RoleSet = 'all' | 'staff' | readonly Role[];

const ADMINS: readonly Role[] = ['super_admin', 'admin'];
const ADMINS_MIS: readonly Role[] = ['super_admin', 'admin', 'mis'];
const COORDINATORS: readonly Role[] = ['super_admin', 'admin', 'mis', 'ra'];
const OFFICE: readonly Role[] = ['super_admin', 'admin', 'mis', 'ra', 'bd'];

/**
 * Upper bound for each (module, action): the roles that may ever hold it.
 * Admins tune the matrix inside these limits; the API refuses grants beyond them.
 */
export const MODULE_CEILINGS: Record<AppModule, Record<PermissionAction, RoleSet>> = {
  Dashboard: { view: 'all', edit: [], delete: [], export: 'staff' },
  // BD raises and reviews requests in BDS (SC-NBD), so only the coordinators use this module in MExP.
  DossierRequests: { view: COORDINATORS, edit: COORDINATORS, delete: COORDINATORS, export: COORDINATORS },
  MyTasks: { view: 'staff', edit: 'staff', delete: [], export: 'staff' },
  // Clients (exporters) never hold Data Bank access. The portal's All dossiers page shows them only their own filed
  // dossiers and certificates, and Makin's accreditation certificates (GET /portal/library, D15).
  DataBank: { view: 'staff', edit: 'staff', delete: COORDINATORS, export: 'staff' },
  TechnicalDocuments: { view: 'staff', edit: 'staff', delete: ADMINS, export: 'staff' },
  Pharmacopoeia: { view: 'staff', edit: 'staff', delete: ADMINS, export: 'staff' },
  Notifications: { view: 'all', edit: [], delete: [], export: [] },
  Exporters: { view: 'staff', edit: COORDINATORS, delete: ADMINS, export: COORDINATORS },
  Users: { view: ADMINS, edit: ADMINS, delete: ['super_admin'], export: ADMINS },
  AccessControl: { view: ADMINS, edit: ADMINS, delete: [], export: [] },
  ActivityLog: { view: ADMINS_MIS, edit: [], delete: [], export: ADMINS_MIS },
  AdminDashboard: { view: ADMINS_MIS, edit: [], delete: [], export: ADMINS_MIS },
  Inquiries: { view: 'all', edit: 'staff', delete: ADMINS, export: 'staff' },
  Registrations: { view: 'all', edit: 'staff', delete: ADMINS, export: 'staff' },
  Dossiers: { view: 'all', edit: 'staff', delete: ADMINS, export: 'staff' },
  Artwork: { view: 'all', edit: 'all', delete: ADMINS, export: 'staff' },
  Samples: { view: 'all', edit: 'staff', delete: ADMINS, export: 'staff' },
};

/** Defaults for a fresh install and for (role, module) pairs the legacy matrix did not define. */
export const DEFAULT_GRANTS: Record<AppModule, Partial<Record<PermissionAction, RoleSet>>> = {
  Dashboard: { view: 'all', export: ADMINS_MIS },
  // "delete" on DossierRequests means cancelling a request; the coordinators who run the flow may do it.
  DossierRequests: { view: COORDINATORS, edit: COORDINATORS, delete: COORDINATORS, export: COORDINATORS },
  MyTasks: { view: 'staff', edit: 'staff' },
  DataBank: { view: 'staff', edit: OFFICE, delete: ADMINS, export: COORDINATORS },
  TechnicalDocuments: { view: 'staff', edit: 'staff', delete: ADMINS },
  Pharmacopoeia: { view: 'staff', edit: COORDINATORS, delete: ADMINS },
  Notifications: { view: 'all' },
  Exporters: { view: OFFICE, edit: COORDINATORS, delete: ADMINS, export: COORDINATORS },
  Users: { view: ADMINS, edit: ADMINS, delete: ['super_admin'], export: ADMINS },
  AccessControl: { view: ADMINS, edit: ADMINS },
  ActivityLog: { view: ADMINS_MIS, export: ADMINS },
  AdminDashboard: { view: ADMINS_MIS },
  Inquiries: { view: OFFICE, edit: ['super_admin', 'admin', 'bd'] },
  Registrations: { view: OFFICE, edit: COORDINATORS },
  Dossiers: { view: OFFICE, edit: COORDINATORS },
  Artwork: { view: [...OFFICE, 'design', 'artwork'], edit: ['super_admin', 'admin', 'design', 'artwork'] },
  Samples: { view: OFFICE, edit: COORDINATORS },
};

function inRoleSet(role: Role, set: RoleSet | undefined): boolean {
  if (!set) return false;
  if (set === 'all') return true;
  if (set === 'staff') return role !== 'exporter';
  return set.includes(role);
}

/** Whether a role may ever hold this permission. */
export function withinCeiling(role: Role, module: AppModule, action: PermissionAction): boolean {
  return inRoleSet(role, MODULE_CEILINGS[module][action]);
}

export function emptyPermission(): ModulePermission {
  return { view: false, edit: false, delete: false, export: false };
}

export function defaultPermission(role: Role, module: AppModule): ModulePermission {
  const grants = DEFAULT_GRANTS[module];
  const result = emptyPermission();
  for (const action of PERMISSION_ACTIONS) {
    result[action] = inRoleSet(role, grants[action]) && withinCeiling(role, module, action);
  }
  return result;
}

/** super_admin holds everything the ceilings allow; the stored matrix is ignored for it. */
export function superAdminPermission(module: AppModule): ModulePermission {
  const result = emptyPermission();
  for (const action of PERMISSION_ACTIONS) result[action] = withinCeiling('super_admin', module, action);
  return result;
}

export function defaultPermissionMap(role: Role): PermissionMap {
  const map = {} as PermissionMap;
  for (const module of APP_MODULES) {
    map[module] = role === 'super_admin' ? superAdminPermission(module) : defaultPermission(role, module);
  }
  return map;
}

export function isAppModule(value: unknown): value is AppModule {
  return typeof value === 'string' && (APP_MODULES as readonly string[]).includes(value);
}
